import ApiService from "@/services/ApiService";
import storageKeys from "@/consts/storageKeys";
import { transformLocalViews } from "@/utils";
import { GlobalView } from "@/classes/views/globalView";
import { LocalView } from "@/classes/views/localView";
import { LOCAL_VIEW_ID_PREFIX, MAIN_VIEW_ID } from "@/consts/consts";

class ViewApi {
  constructor() {
    /** @private */
    this.http = new ApiService();
  }

  /**
   * @param {string} login Current user login
   * @return {Promise<{
   * globalViews: GlobalView[],
   * localView: LocalView[]}>}
   *
   */
  async getViews(login) {
    const globalViews = await this.http.get("named_markups");

    const localViews = this.getLocalViews(login);

    const views = {
      globalViews: globalViews.map(view => new GlobalView(view)),
      localViews
    };

    return views;
  }

  /**
   * @param {string} login Current user login
   * @return {LocalView[]} Array of local views from localStorage
   */
  getLocalViews(login) {
    const localViewsJson = localStorage.getItem(storageKeys.LOCAL_VIEWS);

    if (!localViewsJson) {
      return [];
    }

    const parsedLocalViewsObject = JSON.parse(localViewsJson);

    if (
      Array.isArray(parsedLocalViewsObject) ||
      typeof parsedLocalViewsObject !== "object"
    ) {
      localStorage.removeItem(storageKeys.LOCAL_VIEWS);

      return [];
    }

    const { isChanged, localViewsArray } = transformLocalViews(
      parsedLocalViewsObject[login]
    );

    if (isChanged) {
      localStorage.setItem(
        storageKeys.LOCAL_VIEWS,
        JSON.stringify({
          ...parsedLocalViewsObject,
          [login]: localViewsArray
        })
      );
    }

    return localViewsArray.map(view => new LocalView(view));
  }

  saveView(view, login) {
    return new Promise((resolve, reject) => {
      const localViews =
        JSON.parse(localStorage.getItem(storageKeys.LOCAL_VIEWS)) || {};

      if (localViews && !localViews[login]) {
        localViews[login] = [];
      }

      const copyView = {
        id: view.id,
        name: view.name,
        grid: view.grid,
        cams: view.cams.map(cam => cam.id)
      };

      if (copyView.id === undefined || copyView.id === MAIN_VIEW_ID) {
        copyView.id =
          LOCAL_VIEW_ID_PREFIX +
          localViews[login].reduce((prev, next) => {
            const id = Number(next.id.split(LOCAL_VIEW_ID_PREFIX)[1]);

            return id >= prev ? id + 1 : prev;
          }, 1);

        localViews[login].push(copyView);
      } else {
        const viewIndex = localViews[login].findIndex(
          v => copyView.id === v.id
        );

        if (viewIndex > -1) {
          localViews[login][viewIndex] = copyView;
        } else {
          reject();
        }
      }

      localStorage.setItem(storageKeys.LOCAL_VIEWS, JSON.stringify(localViews));

      resolve(copyView);
    });
  }

  removeView(viewId, login) {
    return new Promise((resolve, reject) => {
      const views = JSON.parse(
        localStorage.getItem(storageKeys.LOCAL_VIEWS) || '{"' + login + '":[]}'
      );

      const viewIndex = views[login].findIndex(view => view.id === viewId);

      if (viewIndex > -1) {
        views[login].splice(viewIndex, 1);

        localStorage.setItem(storageKeys.LOCAL_VIEWS, JSON.stringify(views));

        resolve(viewId);
      } else {
        reject();
      }
    });
  }
}

export const viewApiService = new ViewApi();



// WEBPACK FOOTER //
// ./src/api/view/view.api.js